import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Heatmap, type HeatmapCell, type HeatmapDatum } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { GraphAPI } from '@/api/graph'
import { MAX_WEEKS, formatDaySummary, weeksForWidth } from '@/lib/activityFit'
import { currentStreak } from '@/lib/sigils'
import './activity-heatmap.css'

// Always fetch the widest window the rail can show (19 weeks); the rendered
// week count is measured from the container (see useFitWeeks), and
// buildHeatmapGrid simply ignores days outside a narrower window — so the
// query key stays stable while the grid resizes.
const CELL = 9
const DAYS = MAX_WEEKS * 7

/** Today as ISO yyyy-mm-dd in UTC — matches the backend's day buckets. */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Week columns that actually fit the measured container width. The old
 *  hardcoded 19 overflowed whenever the rail's own vertical scrollbar was a
 *  classic (width-consuming) one; measuring sidesteps every such surprise.
 *  Returns [weeks, attachRef]: the callback ref measures synchronously at
 *  commit (the grid div mounts after the loading state, so an effect keyed on
 *  a ref object would never see it), and a ResizeObserver keeps the count
 *  honest when the rail's inner width changes. */
function useFitWeeks(): [number, (el: HTMLElement | null) => void] {
  const [weeks, setWeeks] = useState(MAX_WEEKS)
  const observer = useRef<ResizeObserver | null>(null)
  const attach = useCallback((el: HTMLElement | null) => {
    observer.current?.disconnect()
    observer.current = null
    if (!el) return
    const apply = () => setWeeks(weeksForWidth(el.clientWidth))
    apply()
    if (typeof ResizeObserver === 'undefined') return
    observer.current = new ResizeObserver(apply)
    observer.current.observe(el)
  }, [])
  return [weeks, attach]
}

function useDailyActivity(domain?: string) {
  const { diwaUrl } = useUI()
  return useQuery({
    queryKey: ['graph-history-daily', domain ?? 'all'],
    queryFn: () => new GraphAPI(diwaUrl).dailyHistory(DAYS, domain),
  })
}

function toHeatmapData(days?: { day: string; total: number }[]): HeatmapDatum[] {
  return (days ?? []).filter((d) => d.total > 0).map((d) => ({ date: d.day, count: d.total }))
}

/** Compact "Jul 12 — 14 events" line for a clicked day, with the cheap warm
 *  path onward: the signals page. */
function DaySummary({ day }: { day: HeatmapCell }) {
  return (
    <div className="dw-activity-day">
      <span>{formatDaySummary(day.date, day.count)}</span>
      <Link className="dw-link" to="/signals">
        view signals →
      </Link>
    </div>
  )
}

/** Home's context tab: your whole verse's activity, at a glance. */
export function HomeContext() {
  const { data, isLoading, isError } = useDailyActivity()
  const [weeks, gridRef] = useFitWeeks()
  const [picked, setPicked] = useState<HeatmapCell | null>(null)
  const total = data?.totals.total ?? 0
  return (
    <div>
      <div className="dw-rail-section-title">Your activity</div>
      {isLoading ? (
        <div className="dw-rail-empty">Reading your history…</div>
      ) : isError ? (
        <div className="dw-rail-empty">Couldn't load your activity — it will retry.</div>
      ) : (
        <>
          <div className="dw-activity-grid" ref={gridRef}>
            <Heatmap
              data={toHeatmapData(data?.days)}
              weeks={weeks}
              endDate={todayIso()}
              unit="events"
              cellSize={CELL}
              legend
              onDayClick={setPicked}
            />
          </div>
          {picked && <DaySummary day={picked} />}
          <div className="dw-activity-summary">
            {total} event{total === 1 ? '' : 's'} in the last {weeks} weeks — every star, link,
            signal and mention you or your familiars added.
          </div>
        </>
      )}
      <div className="dw-activity-links">
        <Link className="dw-link" to="/pricing">Plan →</Link>
        <Link className="dw-link" to="/map">How it connects →</Link>
      </div>
    </div>
  )
}

/**
 * The overall activity heatmap promoted onto the HOME surface (the progression corner).
 * Same source as the rail's context tab (GET /graph/history?granularity=day, whole verse),
 * but sized for the wider main column and paired with the streak line — the heatmap IS the
 * streak surface, and the streak feeds the streak sigil. Cells are a touch larger than the
 * rail's; the week count still measures from the container so it always fits.
 */
export function HomeSurfaceActivity() {
  const { data, isLoading, isError } = useDailyActivity()
  const [weeks, gridRef] = useFitWeeks()
  const [picked, setPicked] = useState<HeatmapCell | null>(null)
  const total = data?.totals.total ?? 0
  const streak = currentStreak(data?.days ?? [])
  return (
    <div className="dw-home-heat">
      {isLoading ? (
        <div className="dw-rail-empty">Reading your history…</div>
      ) : isError ? (
        <div className="dw-rail-empty">Couldn't load your activity — it will retry.</div>
      ) : (
        <>
          <div className="dw-home-heat-streak">
            <span className="dw-home-heat-streak-num">{streak}</span>
            <span className="dw-home-heat-streak-word">
              day{streak === 1 ? '' : 's'} in a row
              {streak === 0 && ' — add anything today to start a streak'}
            </span>
          </div>
          <div className="dw-activity-grid" ref={gridRef}>
            <Heatmap
              data={toHeatmapData(data?.days)}
              weeks={weeks}
              endDate={todayIso()}
              unit="events"
              cellSize={13}
              legend
              onDayClick={setPicked}
            />
          </div>
          {picked && <DaySummary day={picked} />}
          <div className="dw-activity-summary">
            {total} event{total === 1 ? '' : 's'} in the last {weeks} weeks — every star, link,
            signal and mention you or your familiars added. Each active day feeds your streak.
          </div>
        </>
      )}
    </div>
  )
}

/** One domain's slice of the same history — shown in the rail's domain tab. */
export function DomainActivity({ slug }: { slug: string }) {
  const { data, isLoading, isError } = useDailyActivity(slug)
  const [weeks, gridRef] = useFitWeeks()
  const [picked, setPicked] = useState<HeatmapCell | null>(null)
  // Switching domains keeps this component mounted — a picked day from the
  // previous domain would be a lie under the new grid.
  useEffect(() => setPicked(null), [slug])
  if (isLoading || isError) return null // quiet: the domain tab stays useful without it
  const total = data?.totals.total ?? 0
  return (
    <div>
      <div className="dw-rail-section-title">Activity</div>
      <div className="dw-activity-grid" ref={gridRef}>
        <Heatmap
          data={toHeatmapData(data?.days)}
          weeks={weeks}
          endDate={todayIso()}
          unit="events"
          cellSize={CELL}
          legend={false}
          label={`${total} events in ${slug} in the last ${weeks} weeks`}
          onDayClick={setPicked}
        />
      </div>
      {picked && <DaySummary day={picked} />}
      <div className="dw-activity-summary">
        {total} event{total === 1 ? '' : 's'} in this domain in the last {weeks} weeks.
      </div>
    </div>
  )
}
